import type { ReferralAuthPayload, ReferralRewards, StoredReferralAttribution } from '@/types/referrals';

const STORAGE_KEY = 'octo.creator-referral-attribution';
const DEFAULT_ATTRIBUTION_TTL_SECONDS = 7 * 24 * 60 * 60;

interface SaveReferralAttributionInput {
  referralCode: string;
  referralClickId?: string | null;
  campaignKey?: string | null;
  sourceChannel?: string | null;
  creatorDisplayName?: string | null;
  rewards?: ReferralRewards;
  referralStatus?: string;
  attributionTtlSeconds?: number;
}

interface CaptureReferralAttributionInput {
  referralCode: string;
  campaignKey?: string | null;
  sourceChannel?: string | null;
  landingPath?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function loadStoredReferralAttribution(): StoredReferralAttribution | null {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as StoredReferralAttribution;
    if (!parsed.referralCode || !parsed.expiresAt || parsed.expiresAt <= Date.now()) {
      clearStoredReferralAttribution();
      return null;
    }

    return parsed;
  } catch {
    clearStoredReferralAttribution();
    return null;
  }
}

export function saveStoredReferralAttribution(input: SaveReferralAttributionInput): StoredReferralAttribution | null {
  if (!isBrowser() || !input.referralCode) {
    return null;
  }

  const now = Date.now();
  const nextValue: StoredReferralAttribution = {
    referralCode: input.referralCode,
    referralClickId: input.referralClickId ?? null,
    campaignKey: input.campaignKey ?? null,
    sourceChannel: input.sourceChannel ?? null,
    creatorDisplayName: input.creatorDisplayName ?? null,
    rewards: input.rewards,
    referralStatus: input.referralStatus,
    savedAt: now,
    expiresAt: now + (input.attributionTtlSeconds ?? DEFAULT_ATTRIBUTION_TTL_SECONDS) * 1000,
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextValue));
  } catch {
    // 禁用存储时仍把本次归因返回登录表单，不能因此阻断登录。
  }
  return nextValue;
}

export function clearStoredReferralAttribution(): void {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 私密浏览器可能禁止 localStorage；清理不能打断认证。
  }
}

export function buildAuthReferralPayload(
  attribution: StoredReferralAttribution | null,
  referralCodeOverride?: string | null,
): ReferralAuthPayload {
  const normalizedOverride = referralCodeOverride?.trim().toUpperCase();
  const normalizedAttributionCode = attribution?.referralCode?.trim().toUpperCase();

  if (normalizedOverride && normalizedOverride !== normalizedAttributionCode) {
    return {
      referral_code: normalizedOverride,
    };
  }

  if (!attribution && !normalizedOverride) {
    return {};
  }

  const referralCode = normalizedOverride || normalizedAttributionCode;
  if (!referralCode) {
    return {};
  }

  return {
    referral_code: referralCode,
    referral_click_id: attribution?.referralClickId || undefined,
    campaign_key: attribution?.campaignKey || undefined,
  };
}

export async function captureReferralAttribution(
  input: CaptureReferralAttributionInput,
): Promise<StoredReferralAttribution | null> {
  const referralCode = input.referralCode.trim().toUpperCase();
  if (!referralCode) {
    return null;
  }

  const previous = loadStoredReferralAttribution();
  // URL 中的推荐码先保存。公开详情和点击统计都不是绑定的前置条件。
  const provisional = saveStoredReferralAttribution({
    referralCode,
    campaignKey: input.campaignKey,
    sourceChannel: input.sourceChannel,
    ...(previous?.referralCode === referralCode ? {
      referralClickId: previous.referralClickId,
      creatorDisplayName: previous.creatorDisplayName,
      rewards: previous.rewards,
      referralStatus: previous.referralStatus,
    } : {}),
  });
  const { referralsApi } = await import('@/lib/api/referrals');
  const [publicResult, clickResult] = await Promise.allSettled([
    referralsApi.getPublicReferral(referralCode),
    referralsApi.trackClick({
      code: referralCode,
      source_channel: input.sourceChannel,
      campaign_key: input.campaignKey,
      landing_path: input.landingPath,
      utm_source: input.utmSource,
      utm_medium: input.utmMedium,
      utm_campaign: input.utmCampaign,
    }),
  ]);

  const publicRes = publicResult.status === 'fulfilled' ? publicResult.value : null;
  const clickRes = clickResult.status === 'fulfilled' ? clickResult.value : null;
  const publicData = publicRes?.success !== false && !publicRes?.error ? publicRes?.data : undefined;
  const clickData = clickRes?.success !== false && !clickRes?.error ? clickRes?.data : undefined;
  // 较慢的旧请求不能覆盖用户随后打开的新邀请码。
  const current = loadStoredReferralAttribution();
  if (current && current.referralCode !== referralCode) return current;

  return saveStoredReferralAttribution({
    referralCode: publicData?.code ?? referralCode,
    referralClickId: clickData?.click_id ?? provisional?.referralClickId,
    campaignKey: input.campaignKey,
    sourceChannel: input.sourceChannel,
    creatorDisplayName: publicData?.creator.display_name ?? provisional?.creatorDisplayName,
    rewards: publicData?.rewards ?? provisional?.rewards,
    referralStatus: publicData?.status ?? provisional?.referralStatus,
    attributionTtlSeconds: clickData?.attribution_ttl_seconds,
  });
}
